import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { AVAILABLE_APPS } from '../services/appAccess';

interface ProtectedRouteProps {
  children: React.ReactNode;
  appKey?: keyof typeof AVAILABLE_APPS;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, appKey }) => {
  const { isAuthenticated, isLoading, user, checkAppAccess } = useAuth();
  const location = useLocation();
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const [checkingAccess, setCheckingAccess] = useState(false);

  useEffect(() => {
    const checkAccess = async () => {
      if (isAuthenticated && user?.email && appKey) {
        setCheckingAccess(true);
        try {
          // AVAILABLE_APPS is a constant, so we can use it directly
          const access = await checkAppAccess(AVAILABLE_APPS[appKey]);
          setHasAccess(access);
        } catch (error) {
          console.error('Error checking app access:', error);
          setHasAccess(false);
        }
        setCheckingAccess(false);
      } else if (!appKey) {
        // If no appKey is provided, assume access is granted
        setHasAccess(true);
      }
    };

    checkAccess();
  }, [isAuthenticated, user?.email, appKey, checkAppAccess]);

  // Store the attempted URL when not authenticated
  if (!isAuthenticated) {
    localStorage.setItem('redirectUrl', location.pathname);
  }

  // Show loading state
  if (isLoading || checkingAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // Redirect to dashboard if no access to the specific app
  if (appKey && hasAccess === false) {
    return <Navigate to="/dashboard" replace />;
  }

  // Render children when authenticated and has access
  return <>{children}</>;
};

export default ProtectedRoute;
